// src/app/store/useToastStore.ts
import { create } from 'zustand';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'kycVerified' | 'kycPending' | 'kycCanceled' | 'kycRestricted' | 'kycRequiresInput' | 'kycProcessing' | 'payoutPending' | 'payoutActive' | 'payoutDeclined' | 'payoutManualReview' | 'payoutBankPending';
  duration?: number;
  blocking?: boolean; // If true, toast blocks the screen with backdrop
  secondaryMessage?: string; // Optional secondary message line
  action?: ToastAction; // Optional action button
  position?: 'top-right' | 'top-center'; // Defaults to top-right
  wide?: boolean; // Widens the toast beyond the default max width
}

interface ToastStore {
  toasts: Toast[];
  showToast: (
    message: string,
    type?: Toast['type'],
    duration?: number,
    options?: {
      blocking?: boolean;
      secondaryMessage?: string;
      action?: ToastAction;
      position?: 'top-right' | 'top-center';
      wide?: boolean;
    }
  ) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  showToast: (
    message: string,
    type: Toast['type'] = 'info',
    duration = 5000,
    options?: {
      blocking?: boolean;
      secondaryMessage?: string;
      action?: ToastAction;
      position?: 'top-right' | 'top-center';
      wide?: boolean;
    }
  ) => {
    // Deduplicate KYC toasts — only one active toast per KYC type at a time
    const kycTypes: Toast['type'][] = ['kycVerified', 'kycPending', 'kycCanceled', 'kycRestricted', 'kycRequiresInput', 'kycProcessing', 'payoutPending', 'payoutActive', 'payoutDeclined', 'payoutManualReview', 'payoutBankPending'];
    if (kycTypes.includes(type)) {
      const alreadyShowing = useToastStore.getState().toasts.some((t) => t.type === type);
      if (alreadyShowing) return;
    }

    const id = `toast-${Date.now()}-${Math.random()}`;
    const toast: Toast = {
      id,
      message,
      type,
      duration,
      blocking: options?.blocking || false,
      secondaryMessage: options?.secondaryMessage,
      action: options?.action,
      position: options?.position,
      wide: options?.wide,
    };

    set((state) => ({
      toasts: [...state.toasts, toast],
    }));

    // Auto-remove after duration (only if not blocking or has duration set)
    if (duration > 0 && !options?.blocking) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      }, duration);
    }
  },

  removeToast: (id: string) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAllToasts: () => {
    set({ toasts: [] });
  },
}));
